// Vendor Billing v1.0 — "Issue invoice" modal. Reached from BOTH the
// vendor detail VFI Ledger tab (loan picker limited to the vendor's
// assigned loans) and the loan Vendors tab's assignment drawer (loan
// fixed, vendor prefilled). Due date defaults to issue + 30 days
// (VENDOR_INVOICE_DEFAULT_DUE_DAYS, mirroring the server default).

import { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { calendarDateInputToIso, isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { useVendorAssignments } from "@/hooks/portfolioAdmin/useRegistries";
import {
  useContractCheckPreview,
  useIssueVendorInvoice,
} from "@/hooks/portfolioAdmin/useVendorBilling";
import { VENDOR_INVOICE_DEFAULT_DUE_DAYS } from "@/api/portfolioAdmin/types";
import { ContractCheckPanel } from "../accounting/ContractCheckPanel";

interface IssueInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  vendorId: string;
  vendorName: string;
  /** Provided when issuing from the loan Vendors tab — the loan is fixed. */
  fixedLoan?: { loanId: string; label: string };
}

/**
 * The default due date as a wall-calendar date in the USER's timezone.
 *
 * `.toISOString().slice(0,10)` would give today-in-UTC, which is already
 * tomorrow's date for a US user after 5pm — the default silently drifted a day
 * depending on what time of day the form was opened.
 */
function defaultDueDateInput(tz: string): string {
  return isoDateInTz(
    new Date(Date.now() + VENDOR_INVOICE_DEFAULT_DUE_DAYS * 86_400_000),
    tz,
  );
}

export function IssueInvoiceModal({
  open,
  onClose,
  vendorId,
  vendorName,
  fixedLoan,
}: IssueInvoiceModalProps) {
  const toast = useToast();
  const issue = useIssueVendorInvoice();
  const assignmentsQuery = useVendorAssignments(fixedLoan ? undefined : vendorId);

  const loanOptions = useMemo(() => {
    if (fixedLoan) return [fixedLoan];
    const seen = new Set<string>();
    const options: { loanId: string; label: string }[] = [];
    for (const a of assignmentsQuery.data ?? []) {
      if (seen.has(a.loanId)) continue;
      seen.add(a.loanId);
      options.push({ loanId: a.loanId, label: `${a.loanNumber} · ${a.roleOnLoan}` });
    }
    return options;
  }, [assignmentsQuery.data, fixedLoan]);

  const tz = useUserTimeZone();
  const [loanId, setLoanId] = useState(fixedLoan?.loanId ?? "");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(() => defaultDueDateInput(tz));
  const [ackReason, setAckReason] = useState("");

  const selectedLoanId = fixedLoan?.loanId ?? loanId;
  const parsedAmount = Number.parseFloat(amount);
  const amountIsUsable = Number.isFinite(parsedAmount) && parsedAmount > 0;

  // Debounced so typing "1250" does not fire four contract checks.
  const [checkedAmount, setCheckedAmount] = useState<number | undefined>();
  useEffect(() => {
    if (!amountIsUsable) {
      setCheckedAmount(undefined);
      return;
    }
    const t = setTimeout(() => setCheckedAmount(parsedAmount), 400);
    return () => clearTimeout(t);
  }, [amountIsUsable, parsedAmount]);

  const checkQuery = useContractCheckPreview(vendorId, checkedAmount);
  const comparison = checkQuery.data;
  // Only a variance the reviewer is actually looking at can gate the submit.
  // While the check is in flight or has not caught up with the typed amount,
  // the server is still the authority — it re-runs the same gate and 400s.
  const isVariance =
    comparison?.status === "variance" && checkedAmount === parsedAmount;
  const canSubmit =
    !!selectedLoanId && amountIsUsable && (!isVariance || !!ackReason.trim());

  const onSubmit = async () => {
    try {
      const dto = await issue.mutateAsync({
        vendorId,
        loanId: selectedLoanId,
        amount: parsedAmount,
        description: description.trim() || null,
        // A due date is a wall-calendar date, not an instant: noon UTC survives
        // being re-rendered in any timezone as the day that was typed.
        dueDate: calendarDateInputToIso(dueDate),
        varianceAckReason: ackReason.trim() || null,
      });
      toast.success(`Invoice ${dto.invoiceNumber} issued.`);
      setAmount("");
      setDescription("");
      setAckReason("");
      setDueDate(defaultDueDateInput(tz));
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not issue the invoice.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Issue VFI invoice"
      description={`The invoice is numbered, dated, and sent to ${vendorName} when you issue it.`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={!canSubmit || issue.isPending}
            data-testid="issue-invoice-submit"
          >
            Issue invoice
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="issue-invoice-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Loan</span>
          {fixedLoan ? (
            <span className="text-small" data-testid="issue-invoice-fixed-loan">
              {fixedLoan.label}
            </span>
          ) : (
            <select
              className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
              value={loanId}
              onChange={(e) => setLoanId(e.target.value)}
              data-testid="issue-invoice-loan"
            >
              <option value="">
                {assignmentsQuery.isLoading
                  ? "Loading assigned loans…"
                  : loanOptions.length === 0
                    ? "No assigned loans"
                    : "Pick a loan…"}
              </option>
              {loanOptions.map((o) => (
                <option key={o.loanId} value={o.loanId}>{o.label}</option>
              ))}
            </select>
          )}
        </label>

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Amount (USD)</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            className="h-9 px-space-2 border border-border-light rounded-md text-small text-right tabular-nums"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="issue-invoice-amount"
          />
        </label>

        {/* The same contract check the draft queue runs, shown BEFORE submit.
            This path used to issue at any amount with no comparison at all. */}
        {comparison && <ContractCheckPanel comparison={comparison} vendorId={vendorId} />}

        {isVariance && (
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Reason for the difference (required)
            </span>
            <textarea
              className="min-h-[3.5rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
              value={ackReason}
              onChange={(e) => setAckReason(e.target.value)}
              placeholder="e.g. One-off rush work agreed by phone."
              data-testid="issue-invoice-variance-reason"
            />
            <span className="text-caption text-gray-mid">
              The amount is never auto-corrected — it issues as entered, with
              this reason on the record.
            </span>
          </label>
        )}

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Due date</span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            data-testid="issue-invoice-due-date"
          />
          <span className="text-caption text-gray-mid">
            Defaults to {VENDOR_INVOICE_DEFAULT_DUE_DAYS} days after issue.
          </span>
        </label>

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Description</span>
          <textarea
            className="min-h-[4rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Monthly platform fee — June 2026"
            data-testid="issue-invoice-description"
          />
        </label>
      </div>
    </Modal>
  );
}
